import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { CirclePlay, X } from "lucide-react";
import { getDemoVideosForPage } from "../../utils/demoVideos";

const languages = [
  { code: "en", label: "English", field: "en_link" },
  { code: "hi", label: "Hindi", field: "hi_link" },
];
const topicLabel = (topic) => {
  const label = topic.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

function drivePreviewUrl(src) {
  if (!src) return null;
  try {
    const url = new URL(src);
    const parts = url.pathname.split("/");
    return url.protocol === "https:" && url.hostname === "drive.google.com" && parts[1] === "file" && parts[2] === "d" && parts[3]
      ? `https://drive.google.com/file/d/${parts[3]}/preview`
      : null;
  } catch {
    return null;
  }
}
export function FeatureVideoHelp({ title, page }) {
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState(null);
  const [failed, setFailed] = useState(false);
  const demos = getDemoVideosForPage(page);
  const selectedDemo = demos.find((demo) => demo.demo_for_what === selection?.topic);
  const language = languages.find((item) => item.code === selection?.language);
  const src = selectedDemo?.[language?.field];
  const embedSrc = drivePreviewUrl(src);
  const videoTitle = selectedDemo && language ? `${topicLabel(selectedDemo.demo_for_what)} (${language.label})` : "";

  if (!demos.length) return null;

  return (
    <Dialog.Root open={open} onOpenChange={(value) => { setOpen(value); setSelection(null); setFailed(false); }}>
      <Dialog.Trigger asChild>
        <button type="button" aria-label={`Watch demos: ${title}`} title="Watch demos"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/25 bg-primary/5 text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          <CirclePlay className="h-5 w-5" aria-hidden="true" />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-60 max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-border bg-card p-3 shadow-xl sm:p-4">
          <div className="mb-2 flex items-center justify-between gap-4">
            <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" aria-label="Close video" className="rounded-lg p-2 hover:bg-muted"><X className="h-5 w-5" /></button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="mb-3 text-xs text-muted-foreground">
            Choose a topic and language.
          </Dialog.Description>
          <p className="mb-3 text-xs text-muted-foreground sm:hidden">Google Drive videos open in a new tab.</p>
          <div className="mb-3 space-y-2">
            {demos.map((demo) => (
              <div key={demo.demo_for_what} role="group" aria-label={topicLabel(demo.demo_for_what)}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-2">
                <span className="text-sm font-semibold">{topicLabel(demo.demo_for_what)}</span>
                <div className="flex shrink-0 gap-2">
                  {languages.map(({ code, label, field }) => {
                    const selected = selection?.topic === demo.demo_for_what && selection?.language === code;
                    const buttonClass = `rounded-lg border px-3 py-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${selected ? "border-primary bg-primary text-white" : "border-border hover:bg-muted"}`;
                    // Small screens open Drive directly instead of relying on its embedded player.
                    const openDirectly = Boolean(drivePreviewUrl(demo[field]));
                    return (
                      <span key={code} className="inline-flex">
                        {openDirectly && (
                          <a href={demo[field]} target="_blank" rel="noopener noreferrer"
                            aria-label={`${topicLabel(demo.demo_for_what)} in ${label} (opens in a new tab)`}
                            className={`${buttonClass} sm:hidden`}>
                            {label}
                          </a>
                        )}
                        <button type="button" aria-label={`${topicLabel(demo.demo_for_what)} in ${label}`}
                          aria-pressed={selected} disabled={!demo[field]}
                          title={!demo[field] ? `${label} demo is not available yet` : undefined}
                          onClick={() => { setSelection({ topic: demo.demo_for_what, language: code }); setFailed(false); }}
                          className={`${buttonClass} ${openDirectly ? "hidden sm:inline-flex" : ""}`}>
                          {label}
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {open && src && (
            <div>
              <p className="mb-2 text-sm font-semibold" aria-live="polite">{videoTitle}</p>
              {embedSrc ? (
                <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black"><iframe key={embedSrc} title={videoTitle} src={embedSrc} className="absolute inset-0 h-full w-full border-0" allow="autoplay; fullscreen" allowFullScreen /></div>
              ) : (
                <video key={src} className="aspect-video w-full rounded-xl bg-black" src={src} controls playsInline preload="metadata" aria-label={videoTitle} onError={() => setFailed(true)} />
              )}
              {failed && <p role="status" className="mt-3 text-sm text-muted-foreground">This demo could not be loaded. Try opening the video directly.</p>}
              <a href={src} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm text-primary underline">Open video in a new tab</a>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}